import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "V2.0/common/dialog";
import { Button } from "V2.0/common/button";
import { ExtractionParameterTable } from "./ExtractionParameterTable";
import ExtractionParameterEdit from "./ExtractionParameterEdit";
import ExtractionParameterDelete from "./ExtractionParameterDelete";
import ExtractionParameterAdd from "./ExtractionParameterAdd";
import { message } from "antd";
import { putExtraction } from "../../service";
import { Listbox, ListboxOption} from "V2.0/common/listbox";

function ExtractionParameterUpdates(props) {
  const {
    extractionParameters,
    mandatoryFields,
    serializeExtractions,
    onAddExtractionParameter,
    onDeleteExtractionParameter,
    onUpdateExtractionParameter,
    workspaceId,
    assistantId,
    syncAssistant,
    onUpdateMandatoryFieldCondition,
  } = props;

  const [openDialog, setOpenDialog] = useState(false);
  const [openParameterDialog, setOpenParameterDialog] = useState({
    isOpen: false,
    parameter: null,
    index: null,
    mode: null,
  });
  const [loading, setLoading] = useState(false);
  const [isParametersChanged, setIsParametersChanged] = useState(false);

  const { parameter, isOpen, index, mode } = openParameterDialog;
  const openParameterEdit = isOpen && mode === "edit";
  const openParameterDelete = isOpen && mode === "delete";

  const onCloseParameterDialog = () => {
    setOpenParameterDialog({
      isOpen: false,
      parameter: null,
      index: null,
      mode: null,
    });
  };

  const onOpenDialog = () => {
    setOpenDialog(true);
  };

  const onCloseDialog = () => {
    setOpenDialog(false);
    setIsParametersChanged(false);
  };

  async function handleSaveParameters() {
    message.loading("Updating Extraction Fields");
    setLoading(true);

    const serializedExtractions = serializeExtractions(extractionParameters);

    const payload = {
      extractionParameters: serializedExtractions,
      mandatoryFields,
    };

    try {
      const response = await putExtraction({
        workspaceId,
        assistantId,
        payload,
      });

      if (response.status === 200) {
        syncAssistant((prevAssistant) => {
          return {
            ...prevAssistant,
            extraction: response.data,
          };
        });
        message.destroy();
        message.success("Extraction fields updated");
        setLoading(false);
        onCloseDialog();
      }
    } catch (e) {
      message.error(
        e.response.data?.msg || e.response.data?.message || e.response.data?.error || "Failed to update extraction fields"
      );
      setLoading(false);
    }
  }

  return (
    <>
      <Button onClick={onOpenDialog}>Update Fields</Button>
      {openDialog && (
        <Dialog size="6xl" className="" open={openDialog} onClose={() => {}}>
          <DialogTitle className="">
            <div className="flex items-center justify-between">
              <p>Fields {`(${extractionParameters.length})`}</p>
              <ExtractionParameterAdd
                onAddExtractionParameter={(data) => {
                  onAddExtractionParameter(data);
                  setIsParametersChanged(true);
                }}
              />
            </div>
          </DialogTitle>
          <DialogBody className="h-[70dvh] max-h-[70dvh] overflow-scroll">
            <div className="flex flex-col gap-y-8">
              <div className="flex flex-col w-2/4 items-start gap-y-2">
                <p className="text-sm font-semibold text-gray-500">
                  Extraction Criteria:
                </p>
                <Listbox
                  as="div"
                  className="my-2"
                  onChange={(value) => {
                    onUpdateMandatoryFieldCondition(value);
                    setIsParametersChanged(true);
                  }}
                  value={mandatoryFields.condition}
                >
                  {[
                    {
                      label: "Atleast one mandatory field should be present",
                      value: "OR",
                    },
                    {
                      label: "All the mandatory fields should be present",
                      value: "AND",
                    },
                  ].map((condition) => {
                    return (
                      <ListboxOption key={condition.value} value={condition.value}>
                        {condition.label}
                      </ListboxOption>
                    );
                  })}
                </Listbox>
              </div>
              <div className="h-[70dvh] max-h-[70dvh] overflow-scroll">
                <ExtractionParameterTable
                  extractionParameters={extractionParameters}
                  showActions={true}
                  onEdit={(item, index) => {
                    setOpenParameterDialog({
                      parameter: item,
                      index,
                      isOpen: true,
                      mode: "edit",
                    });
                  }}
                  onDelete={(item, index) => {
                    setOpenParameterDialog({
                      parameter: item,
                      index,
                      isOpen: true,
                      mode: "delete",
                    });
                  }}
                />
              </div>
            </div>
          </DialogBody>
          <DialogActions>
            <Button outline onClick={onCloseDialog}>
              Cancel
            </Button>
            <Button
              disabled={!isParametersChanged}
              loading={loading}
              onClick={handleSaveParameters}
            >
              Save Fields
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {openParameterEdit && (
        <ExtractionParameterEdit
          extractionParameter={parameter}
          onClose={onCloseParameterDialog}
          onSave={(data) => {
            onUpdateExtractionParameter(data, index);
            setIsParametersChanged(true);
          }}
        />
      )}
      {openParameterDelete && (
        <ExtractionParameterDelete
          extractionParameter={parameter}
          onClose={onCloseParameterDialog}
          onDelete={() => {
            onDeleteExtractionParameter(parameter, index);
            setIsParametersChanged(true);
          }}
        />
      )}
    </>
  );
}

export { ExtractionParameterUpdates };
